import { Button, Text } from "@chakra-ui/react";
import { useCallback, useRef, useState, type ReactNode } from "react";
import {
  ConfirmContext,
  type ConfirmFn,
  type ConfirmOptions,
} from "./useConfirm";
import {
  DialogActionTrigger,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "../chakra/dialog";

export const ConfirmProvider = ({ children }: { children: ReactNode }) => {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    resolver.current?.(false);
    setOptions(opts);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = (value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setOptions(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <DialogRoot
        isOpen={options !== null}
        onClose={() => close(false)}
        isCentered
        size="sm"
        role="alertdialog"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{options?.title}</DialogTitle>
          </DialogHeader>
          {options?.description && (
            <DialogBody>
              {typeof options.description === "string" ? (
                <Text color="fg.muted">{options.description}</Text>
              ) : (
                options.description
              )}
            </DialogBody>
          )}
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button variant="outline">
                {options?.cancelLabel ?? "Cancelar"}
              </Button>
            </DialogActionTrigger>
            <Button
              colorPalette={options?.colorPalette ?? "brand"}
              onClick={() => close(true)}
              autoFocus
            >
              {options?.confirmLabel ?? "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </ConfirmContext.Provider>
  );
};
